// KioskCombobox — touch-first searchable picker for the kiosk Step-2 fields
// (vehicle, haulier, driver).
//
// Mirrors the interaction model of the main module's WbProductCombobox
// (type to filter, ArrowUp/Down to move the highlight, Enter to confirm,
// click-outside / Escape to close, ✕ to clear) but renders entirely from the
// kiosk's var(--kiosk-*) theme tokens so it re-skins with the dark/light
// toggle (DECISION-021). Generic over option shape: each option is a stable
// scalar id + a label (+ optional sublabel).
//
// `selectedLabelFallback` covers a hydrated selection whose option isn't in
// the loaded list — e.g. an existing ticket's haulier/driver/vehicle, whose
// enc_id ciphertext won't match any list option because entity enc_ids
// re-encrypt with a random IV on every response (DECISION-016). The picker
// still shows the name; re-picking from the list yields a fresh enc_id.

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

import styles from './kiosk.module.css';

export interface KioskComboOption {
  readonly id: string;
  readonly label: string;
  readonly sublabel?: string | null;
}

export interface KioskComboboxProps {
  readonly options: ReadonlyArray<KioskComboOption>;
  /** Currently-selected option id (or null). */
  readonly value: string | null;
  readonly onChange: (id: string | null) => void;
  /** Label to show when `value` is set but not present in `options`. */
  readonly selectedLabelFallback?: string | null;
  readonly placeholder?: string;
  readonly ariaLabel?: string;
  readonly disabled?: boolean;
  readonly loading?: boolean;
  readonly emptyText?: string;
  readonly 'data-testid'?: string;
}

export function KioskCombobox({
  options,
  value,
  onChange,
  selectedLabelFallback = null,
  placeholder = 'Search…',
  ariaLabel = 'Search',
  disabled = false,
  loading = false,
  emptyText = 'No matches.',
  'data-testid': testId,
}: KioskComboboxProps): JSX.Element {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const rootRef = useRef<HTMLDivElement | null>(null);

  const selected = useMemo(
    () => (value !== null ? options.find((o) => o.id === value) ?? null : null),
    [options, value],
  );
  const selectedLabel =
    selected?.label ?? (value !== null ? selectedLabelFallback ?? 'Selected' : null);

  // Filter by label OR sublabel while typing. Empty query shows the list.
  const filtered = useMemo(() => {
    const q = text.trim().toLowerCase();
    if (q === '') return options.slice(0, 100);
    return options
      .filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          (o.sublabel ?? '').toLowerCase().includes(q),
      )
      .slice(0, 100);
  }, [options, text]);

  useEffect(() => {
    setActiveIndex(0);
  }, [text]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    function handle(e: MouseEvent) {
      if (!rootRef.current) return;
      if (e.target instanceof Node && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  function select(o: KioskComboOption): void {
    onChange(o.id);
    setText('');
    setOpen(false);
  }

  function clear(): void {
    onChange(null);
    setText('');
    setOpen(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      const pick = filtered[activeIndex];
      if (open && pick) {
        e.preventDefault();
        select(pick);
      }
    }
  }

  // While an option is selected and the user isn't typing, show its label;
  // typing switches to search mode. Keyed on `value` so a hydrated selection
  // absent from the list still shows its fallback label rather than blanking.
  const displayValue = value !== null && text === '' ? selectedLabel ?? '' : text;

  return (
    <div ref={rootRef} className={styles.combo} data-testid={testId}>
      <div className={styles.comboInputRow}>
        <input
          className={styles.comboInput}
          type="text"
          role="combobox"
          value={displayValue}
          disabled={disabled}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            // Typing while an option is selected clears it — the operator has
            // signalled intent to re-pick.
            if (value !== null) onChange(null);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-autocomplete="list"
          aria-expanded={open}
          autoComplete="off"
        />
        {value !== null && !disabled ? (
          <button
            type="button"
            className={styles.comboClear}
            onClick={clear}
            aria-label={`Clear ${ariaLabel}`}
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            ✕
          </button>
        ) : null}
      </div>

      {open && !disabled ? (
        <div className={styles.comboDropdown} role="listbox" aria-label={`${ariaLabel} options`}>
          {loading ? (
            <div className={styles.comboStatus}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div className={styles.comboStatus}>{emptyText}</div>
          ) : (
            filtered.map((o, idx) => (
              <button
                key={o.id}
                type="button"
                role="option"
                aria-selected={o.id === value}
                className={`${styles.comboOption} ${
                  idx === activeIndex ? styles.comboOptionActive : ''
                }`}
                // mousedown (not click) so the selection lands before the
                // input's blur/outside-click handler closes the dropdown.
                onMouseDown={(e) => {
                  e.preventDefault();
                  select(o);
                }}
                onMouseEnter={() => setActiveIndex(idx)}
              >
                <span>{o.label}</span>
                {o.sublabel ? <span className={styles.comboOptionSub}>{o.sublabel}</span> : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
